import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, Square, SwitchCamera, RotateCw, MoreHorizontal, Sparkles, Camera as CameraIcon, Video as VideoIcon, MonitorPlay, ScanFace, Maximize2, ZoomIn } from "lucide-react";
import useAutoFrame from "../hooks/useAutoFrame";

/**
 * MobileLivePreviewBar — sticky-top camera surface for mobile Director's
 * Mode. Always renders the host's live camera at the top of the viewport
 * (across every tab) once they're broadcasting, so they never lose sight
 * of their own preview when they switch to Audio / Overlays / Guests /
 * Chat tabs.
 *
 * When NOT broadcasting yet, renders a simulated scene preview + an
 * inline "Start Camera" CTA so the user can flip into WebRTC mode
 * without scrolling down.
 *
 * Hidden on lg+ desktop so the existing two-column layout is untouched.
 *
 * Props:
 *   - active: live-stream object (title, scene, invite_code, etc.)
 *   - broadcasterProxy: proxy from <AdvancedDirectorPanel onBroadcasterReady>
 *   - webrtcMode: boolean — am I currently broadcasting via WebRTC?
 *   - onStartCamera: () => void — flip webrtcMode to true (mounts the broadcaster)
 *   - onEndCamera: () => void — flip webrtcMode to false (unmounts the broadcaster)
 *   - onEndStream: () => void — fully end the stream (closes the live row)
 *   - viewerCount: number
 *   - onSwitchTab: (tabId) => void — ask the parent to navigate to a tab
 */
export default function MobileLivePreviewBar({
  active,
  broadcasterProxy,
  webrtcMode,
  onStartCamera,
  onEndCamera,
  onEndStream,
  viewerCount = 0,
  onSwitchTab,
}) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [showCamMenu, setShowCamMenu] = useState(false);
  const [rotationDeg, setRotationDeg] = useState(0);
  const [mirror, setMirror] = useState(false);
  const [resolution, setResolution] = useState("hd"); // "hd" 1080p | "ld" 720p | "uhd" 1440p
  const [hdBusy, setHdBusy] = useState(false);
  const [autoFrame, setAutoFrame] = useState(false);
  // Camera zoom 1.0 (no zoom) → 3.0 (3x). Applied via MediaStreamTrack
  // constraints when the platform supports it (most modern Android +
  // rear iOS cameras); falls back to a CSS transform scale for phones
  // whose camera driver doesn't expose `zoom` (iOS Safari front-cam,
  // desktop webcams). Both approaches produce the same visual result.
  const [zoom, setZoom] = useState(1);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [showZoomSlider, setShowZoomSlider] = useState(false);
  // Landscape detection — on mobile landscape the 16:9 preview eats
  // the whole viewport height and the control panel beneath becomes
  // inaccessible. We shorten the preview and make the header more
  // compact so the rest of the UI is usable.
  const [isLandscape, setIsLandscape] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(orientation: landscape) and (max-height: 500px)");
    const handler = () => setIsLandscape(mq.matches);
    handler();
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Detect zoom-constraint support on the current video track when the
  // stream arrives. Chrome on Android + Safari on iPhone Pro back cams
  // expose `{ zoom: { min, max, step } }` in track.getCapabilities().
  useEffect(() => {
    const t = stream?.getVideoTracks?.()[0];
    if (!t || !t.getCapabilities) { setZoomSupported(false); return; }
    try {
      const caps = t.getCapabilities();
      setZoomSupported(!!caps.zoom);
    } catch { setZoomSupported(false); }
  }, [stream]);

  // Apply zoom changes — prefer MediaStreamTrack constraint (true
  // optical/digital camera zoom, no quality loss); fall back to CSS
  // transform scale when the track doesn't support it.
  useEffect(() => {
    const t = stream?.getVideoTracks?.()[0];
    if (!t || !zoomSupported) return;
    try {
      const caps = t.getCapabilities();
      const clamped = Math.max(caps.zoom.min || 1, Math.min(caps.zoom.max || 3, zoom));
      t.applyConstraints({ advanced: [{ zoom: clamped }] }).catch(() => {});
    } catch { /* ignore */ }
  }, [zoom, stream, zoomSupported]);

  // Center-Stage style smart framing — only active when the toggle is on
  // AND we actually have a stream playing. Updates `object-position` so
  // the host's face stays roughly in the centre of the visible crop.
  const auto = useAutoFrame(videoRef, autoFrame && !!stream);

  // Pull the live MediaStream out of the broadcaster as soon as it
  // exists, then bind it to our mirror <video>. Polling is cheap (every
  // 600ms) and stops once we have a stream that's still active. We
  // compare VIDEO TRACK IDS (not MediaStream identity) so when the
  // broadcaster returns a freshly-composed stream each call (e.g. after
  // green-screen toggles on/off), we only re-bind srcObject when the
  // underlying track actually changed.
  useEffect(() => {
    if (!broadcasterProxy) return;
    let cancelled = false;
    const tick = () => {
      const s = broadcasterProxy.getPreviewStream?.() || broadcasterProxy.getMediaStream?.() || null;
      if (cancelled) return;
      const prevId = stream?.getVideoTracks?.()[0]?.id || null;
      const nextId = s?.getVideoTracks?.()[0]?.id || null;
      if (prevId !== nextId || (!stream && s) || (stream && !s)) {
        setStream(s);
      }
    };
    tick();
    const id = setInterval(tick, 600);
    return () => { cancelled = true; clearInterval(id); };
  }, [broadcasterProxy, stream]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (stream && v.srcObject !== stream) {
      try { v.srcObject = stream; v.play().catch(() => {}); } catch {}
    } else if (!stream && v.srcObject) {
      try { v.srcObject = null; } catch {}
    }
  }, [stream]);

  const flipCam = useCallback(() => {
    broadcasterProxy?.flipCamera?.();
  }, [broadcasterProxy]);

  const rotate = useCallback(() => {
    setRotationDeg((d) => (d + 90) % 360);
  }, []);

  const cycleResolution = useCallback(async () => {
    if (!broadcasterProxy?.reacquireCamera) return;
    setHdBusy(true);
    const order = ["ld", "hd", "uhd"];
    const next = order[(order.indexOf(resolution) + 1) % order.length];
    const target = next === "ld"
      ? { width: 1280, height: 720, fps: 30 }
      : next === "hd"
        ? { width: 1920, height: 1080, fps: 30 }
        : { width: 2560, height: 1440, fps: 30 };
    try {
      await broadcasterProxy.reacquireCamera(target);
      setResolution(next);
    } finally {
      setHdBusy(false);
    }
  }, [broadcasterProxy, resolution]);

  const onAir = webrtcMode && !!stream;
  const resolutionLabel = resolution === "ld" ? "720p" : resolution === "hd" ? "1080p" : "1440p";

  const transformStyle = {
    // CSS zoom fallback — only multiplied in when the camera track
    // doesn't natively support it, so we don't double-zoom on phones
    // that already zoomed the underlying stream.
    transform: `rotate(${rotationDeg}deg) scaleX(${mirror ? -1 : 1}) scale(${(!zoomSupported && zoom) || 1}) ${autoFrame ? "scale(1.12)" : ""}`,
    transformOrigin: "center center",
    objectPosition: autoFrame ? `${auto.x}% ${auto.y}%` : "center",
    transition: "object-position 320ms ease-out",
  };

  return (
    <div
      data-testid="mobile-live-preview-bar"
      className="lg:hidden sticky top-16 z-30 bg-black/95 backdrop-blur-md border-b border-white/10"
    >
      {/* Header strip — title, viewer count, full End-Stream button */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
        <span data-testid="mobile-live-pill" className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1 shrink-0 ${onAir ? "bg-tv-live text-white" : "bg-white/10 text-white/70"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${onAir ? "bg-white tv-pulse" : "bg-white/50"}`} /> {onAir ? "LIVE" : "READY"}
        </span>
        <div className="font-display font-bold text-white truncate flex-1 text-sm" title={active?.title}>{active?.title || "Untitled"}</div>
        <span className="text-[10px] text-white/60 font-bold shrink-0">{viewerCount.toLocaleString()} 👁</span>
        <button
          type="button"
          onClick={() => { if (active?.id) navigate(`/studio/console/${active.id}`); }}
          disabled={!active?.id}
          data-testid="mobile-full-studio-btn"
          title="Open the immersive desktop-style Director Console with full FX + overlays"
          className="bg-tv-accent hover:bg-yellow-300 text-black font-black uppercase tracking-wider text-[10px] px-2.5 py-1 rounded-full flex items-center gap-1 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Maximize2 className="w-3 h-3" /> Studio
        </button>
        <button
          type="button"
          onClick={onEndStream}
          data-testid="mobile-end-stream-btn"
          className="bg-tv-live hover:bg-red-600 text-white font-black uppercase tracking-wider text-[10px] px-2.5 py-1 rounded-full flex items-center gap-1 shrink-0"
        >
          <Square className="w-3 h-3 fill-current" /> End
        </button>
      </div>

      <div className={`relative w-full bg-[#0B1120] overflow-hidden ${isLandscape ? "h-[38vh]" : "aspect-video"}`}>
        {/* Mirror video — only renders when we have a live MediaStream.
            Two video elements (this + the one inside <LiveBroadcaster>
            inside the audio tab) read the same stream — that's allowed
            and standard browser behavior. */}
        {onAir ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={transformStyle}
            data-testid="mobile-preview-video"
          />
        ) : (
          <>
            {active?.thumbnail_url ? (
              <img src={active.thumbnail_url} alt="" className="w-full h-full object-cover opacity-60" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#160d28] via-[#0e1129] to-black" />
            )}
            <div className="absolute inset-0 flex items-center justify-center flex-col gap-3 p-4">
              <div className="bg-black/70 px-3 py-1.5 rounded-md text-white font-display text-sm" data-testid="mobile-preview-scene">
                Scene · <span className="text-tv-brand uppercase">{active?.scene || "camera"}</span>
              </div>
              <button
                type="button"
                onClick={onStartCamera}
                data-testid="mobile-start-camera-btn"
                className="bg-tv-live hover:bg-red-600 text-white font-black uppercase tracking-wider text-xs px-5 py-2.5 rounded-full flex items-center gap-1.5 shadow-[0_0_20px_rgba(220,38,38,0.5)]"
              >
                <Radio className="w-4 h-4" /> Start Camera
              </button>
              <div className="text-[10px] uppercase tracking-[0.25em] text-tv-accent font-bold">Tap to go on air</div>
            </div>
          </>
        )}

        {/* ON AIR pill */}
        {onAir && (
          <div className="absolute top-2 left-2 flex items-center gap-1.5">
            <span data-testid="mobile-preview-onair" className="bg-tv-live text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-white tv-pulse" /> ON AIR
            </span>
            <span className="bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded">
              {viewerCount.toLocaleString()} 👁
            </span>
          </div>
        )}

        {/* Camera quick-controls cluster — top-right corner */}
        {onAir && (
          <div className="absolute top-2 right-2 flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={flipCam}
                data-testid="mobile-cam-flip"
                title="Flip camera (front ↔ back)"
                className="w-8 h-8 rounded-full bg-black/70 hover:bg-black/85 text-white flex items-center justify-center backdrop-blur"
              >
                <SwitchCamera className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={rotate}
                data-testid="mobile-cam-rotate"
                title={`Rotate preview (${rotationDeg}°)`}
                className="w-8 h-8 rounded-full bg-black/70 hover:bg-black/85 text-white flex items-center justify-center backdrop-blur"
              >
                <RotateCw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={cycleResolution}
                disabled={hdBusy}
                data-testid="mobile-cam-resolution"
                title={`Resolution — tap to cycle. Currently ${resolutionLabel}`}
                className="px-2 h-8 rounded-full bg-black/70 hover:bg-black/85 text-white flex items-center gap-1 backdrop-blur text-[10px] font-black uppercase tracking-wider disabled:opacity-50"
              >
                <Sparkles className="w-3.5 h-3.5" /> {hdBusy ? "…" : resolutionLabel}
              </button>
              <button
                type="button"
                onClick={() => setShowZoomSlider((v) => !v)}
                data-testid="mobile-cam-zoom"
                title={zoomSupported ? "Optical/digital camera zoom" : "Preview zoom (CSS fallback)"}
                aria-expanded={showZoomSlider}
                className={`px-2 h-8 rounded-full ${showZoomSlider ? "bg-tv-accent text-black" : "bg-black/70 hover:bg-black/85 text-white"} flex items-center gap-1 backdrop-blur text-[10px] font-black uppercase tracking-wider`}
              >
                <ZoomIn className="w-3.5 h-3.5" />
                {zoom > 1 ? `${zoom.toFixed(1)}x` : "1x"}
              </button>
              <button
                type="button"
                onClick={() => setShowCamMenu((v) => !v)}
                data-testid="mobile-cam-more"
                title="More camera options"
                aria-expanded={showCamMenu}
                className="w-8 h-8 rounded-full bg-black/70 hover:bg-black/85 text-white flex items-center justify-center backdrop-blur"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
            {showCamMenu && (
              <div data-testid="mobile-cam-menu" className="bg-black/95 border border-white/10 rounded-xl px-2 py-1.5 backdrop-blur-md flex flex-col gap-0.5 min-w-[180px]">
                <button
                  type="button"
                  onClick={() => { setAutoFrame((v) => !v); setShowCamMenu(false); }}
                  data-testid="mobile-cam-autoframe"
                  className="text-[11px] font-bold uppercase tracking-wider text-white/85 hover:text-white px-2 py-1.5 rounded text-left flex items-center justify-between gap-2"
                  title={auto.supported ? "Track face & keep host centered (Center Stage style)" : "Center-lock framing (face tracking unavailable in this browser)"}
                >
                  <span className="flex items-center gap-1.5">
                    <ScanFace className="w-3.5 h-3.5" /> Auto-frame
                    {!auto.supported && <span className="text-[8px] text-white/40">(center-lock)</span>}
                  </span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${autoFrame ? "bg-tv-accent text-black" : "bg-white/10"}`}>
                    {autoFrame ? (auto.hasFace ? "TRACK" : "ON") : "OFF"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => { setMirror((m) => !m); setShowCamMenu(false); }}
                  data-testid="mobile-cam-mirror"
                  className="text-[11px] font-bold uppercase tracking-wider text-white/80 hover:text-white px-2 py-1.5 rounded text-left flex items-center justify-between"
                >
                  Mirror preview <span className={`text-[9px] px-1.5 py-0.5 rounded ${mirror ? "bg-tv-accent text-black" : "bg-white/10"}`}>{mirror ? "ON" : "OFF"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setRotationDeg(0); setShowCamMenu(false); }}
                  className="text-[11px] font-bold uppercase tracking-wider text-white/80 hover:text-white px-2 py-1.5 rounded text-left"
                >
                  Reset rotation
                </button>
                <button
                  type="button"
                  onClick={() => { onSwitchTab?.("audio"); setShowCamMenu(false); }}
                  className="text-[11px] font-bold uppercase tracking-wider text-tv-accent hover:text-yellow-300 px-2 py-1.5 rounded text-left"
                >
                  Open Audio mixer →
                </button>
                <button
                  type="button"
                  onClick={() => { onSwitchTab?.("guests"); setShowCamMenu(false); }}
                  className="text-[11px] font-bold uppercase tracking-wider text-tv-accent hover:text-yellow-300 px-2 py-1.5 rounded text-left"
                >
                  Manage guests →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Zoom slider overlay — appears over the preview when the user
            taps the zoom chip. Centered at the bottom so it's within
            thumb reach on mobile. */}
        {onAir && showZoomSlider && (
          <div
            data-testid="mobile-zoom-slider-panel"
            className="absolute bottom-12 left-1/2 -translate-x-1/2 bg-black/90 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 flex items-center gap-3 z-10"
          >
            <span className="text-[9px] font-black text-white/60 uppercase tracking-wider">1x</span>
            <input
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              data-testid="mobile-zoom-slider"
              className="w-40 accent-tv-accent"
              aria-label="Camera zoom"
            />
            <span className="text-[10px] font-black text-tv-accent tabular-nums w-8 text-right">{zoom.toFixed(1)}x</span>
            {zoom !== 1 && (
              <button
                type="button"
                onClick={() => setZoom(1)}
                className="text-[9px] font-black text-white/60 hover:text-white uppercase tracking-wider"
              >
                Reset
              </button>
            )}
          </div>
        )}

        {/* End-camera control — bottom-right when on air */}
        {onAir && (
          <button
            type="button"
            onClick={onEndCamera}
            data-testid="mobile-end-camera-btn"
            className="absolute bottom-2 right-2 bg-tv-live hover:bg-red-600 text-white font-black uppercase tracking-wider text-[10px] px-2.5 py-1.5 rounded-full flex items-center gap-1.5"
          >
            <Square className="w-3 h-3 fill-current" /> Stop cam
          </button>
        )}

        {/* Lower-third style title pill — bottom-left always */}
        <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm rounded-md px-2 py-1 max-w-[60%]">
          <div className="text-[8px] uppercase tracking-[0.25em] text-tv-accent font-bold leading-none">{onAir ? "Live Broadcast" : "Standing By"}</div>
          <div className="text-white text-[11px] font-bold truncate leading-tight mt-0.5" data-testid="mobile-preview-title">{active?.title || "Untitled"}</div>
        </div>
      </div>
    </div>
  );
}
